import { useEffect, useState, useRef } from 'react';
import { Share2, ShoppingCart, Coins, BellRing, LayoutDashboard, CalendarCheck, ShieldCheck, CheckCircle2, Sparkles } from 'lucide-react';
import Footer from '../components/Footer';
import SceneDeck from '../components/SceneDeck';
import CountryPhoneInput from '../components/CountryPhoneInput';
import { trackPageView, trackEvent, flushEvents } from '../lib/analytics';

// ============================================================
// /partner 小B 商機招募（SceneDeck 老虎機拉桿版，規則 59）
// 對象：移居海外港人（工作機會少、有港人圈子）——
// 「開心分享 → 終身綁定佣金 → 被動收入」招募推廣夥伴
// 收口：表單（姓名/WhatsApp 國際號/所在城市）→ godfather join
// （source=partner，自動建 referrer＋dashboard）；零門檻，後台可停用
// ============================================================

const STEPS = [
  { n: '01', title: '分享俾圈內朋友', desc: '你嘅專屬連結，share 入 group、send 俾朋友——開心分享，唔使推銷' },
  { n: '02', title: '朋友落單', desc: '朋友撳你條 link 買香港貨——YYC 包稅直郵寄到佢屋企' },
  { n: '03', title: '佣金即時入你帳', desc: '朋友一落單，10% 佣金即時入帳——你唔使做跟單、唔使處理物流' },
];

const PASSIVE = [
  { icon: <BellRing size={30} />, title: 'done deal 即時通知', desc: '朋友落單，WhatsApp 即時話你知——每單實時到手' },
  { icon: <LayoutDashboard size={30} />, title: '後台 24 小時可查', desc: '專屬推廣後台——佣金、訂單、紀錄隨時睇' },
  { icon: <CalendarCheck size={30} />, title: '每月分賬過數', desc: '佣金月月結算過數——被動收入自動入袋' },
];

export default function Partner() {
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [consent, setConsent] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ referralCode: string } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => { trackPageView('/partner'); }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError(null);
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 8) {
      setError('請輸入有效嘅 WhatsApp 號碼（含國家碼）');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch('/api/public/godfather/join', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json; charset=utf-8' },
        body: JSON.stringify({ name, phone: digits, city, source: 'partner', marketingConsent: consent }),
      });
      const data = await res.json().catch(() => ({}));
      const payload = data.data || data;
      if (!res.ok) {
        trackEvent('partner_submit', { status: res.status === 409 || res.status === 429 ? 'dup' : 'error' });
        flushEvents();
        throw new Error(payload.error || data.error || '提交失敗，請稍後再試');
      }
      trackEvent('partner_submit', { status: 'ok' });
      trackEvent('godfather_join', { source: 'partner' });
      flushEvents();
      setDone({ referralCode: payload.referralCode || '' });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '提交失敗，請稍後再試');
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = 'w-full px-4 py-3 border border-stone-300 rounded-xl focus:ring-2 focus:ring-x1493-primary outline-none bg-white text-stone-800';

  // 四幕（SceneDeck 老虎機拉桿版）
  const scenes = [
    {
      id: 'hook',
      reels: ['圈子', '分享', '佣金'] as [string, string, string],
      render: () => (
        <section className="relative min-h-full text-white overflow-hidden">
          <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(/assets/stock/hero-hk-neon.jpg)' }} />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(100deg, rgba(11,53,39,.96) 0%, rgba(11,53,39,.82) 55%, rgba(11,53,39,.45) 100%)' }} />
          <div className="max-w-5xl mx-auto px-6 py-20 relative z-10">
            <p className="text-x1493-secondary font-bold tracking-widest text-sm mb-4">寫俾移居海外嘅香港人</p>
            <h1 className="text-3xl md:text-5xl font-bold leading-tight mb-6">
              移居海外，搵工難？<br className="hidden md:block" />
              ——你嘅港人圈子，就係你嘅資產。
            </h1>
            <p className="text-white/80 text-lg max-w-2xl leading-relaxed mb-2">
              你身邊圍住嘅，係成千上萬個掛住香港味道、掛住香港貨嘅人。
            </p>
            <p className="text-x1493-secondary text-xl md:text-2xl font-bold max-w-2xl leading-snug">
              開心分享 → 終身綁定佣金 → 被動收入。零門檻，即刻開始。
            </p>
            <p className="text-white/50 text-sm mt-10 animate-pulse">🔻 拉下面支桿——一頁一頁睇</p>
          </div>
        </section>
      ),
    },
    {
      id: 'how',
      reels: ['分享', '落單', '佣金'] as [string, string, string],
      render: () => (
        <section className="min-h-full bg-stone-50 flex items-center py-16">
          <div className="max-w-5xl mx-auto px-6 w-full">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold mb-3 text-stone-900">開心分享，三步搞定</h2>
              <p className="text-stone-500 max-w-2xl mx-auto">唔使入貨、唔使收款、唔使處理售後—— YYC 一腳踢，你只負責分享。</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {STEPS.map((s) => (
                <div key={s.n} className="bg-white border border-stone-200 rounded-3xl p-7 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-x1493-primary/10 text-x1493-primary flex items-center justify-center mx-auto mb-4">{s.n}</div>
                  <p className="font-mono text-xs text-x1493-secondary font-bold tracking-widest mb-1">{s.n}</p>
                  <h3 className="font-bold text-lg mb-2 text-stone-900">{s.title}</h3>
                  <p className="text-sm text-stone-500 leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
            <div className="mt-8 rounded-3xl p-7 md:p-8 text-white text-center" style={{ background: 'linear-gradient(120deg, #0b3527 0%, #12503c 60%, #1a6b50 100%)' }}>
              <h3 className="text-xl md:text-2xl font-bold mb-3">♾️ 終身綁定</h3>
              <p className="text-white/85 max-w-2xl mx-auto leading-relaxed">
                朋友經你條 link 落第一次單，就同你<b className="text-x1493-secondary">終身綁定</b>——
                佢日後每次消費，都同你有關。今日分享一次，聽日仲喺度幫你賺錢。
              </p>
            </div>
          </div>
        </section>
      ),
    },
    {
      id: 'passive',
      reels: ['通知', '後台', '月結'] as [string, string, string],
      render: () => (
        <section className="min-h-full bg-white flex items-center py-16">
          <div className="max-w-5xl mx-auto px-6 w-full">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold mb-3 text-stone-900">被動收入，唔使估唔使等</h2>
              <p className="text-stone-500 max-w-2xl mx-auto">你瞓緊覺，圈子都喺度幫你賺錢——一切透明可查。</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {PASSIVE.map((p) => (
                <div key={p.title} className="border border-stone-200 rounded-3xl p-7 text-center hover:border-x1493-primary/40 transition">
                  <div className="w-14 h-14 rounded-full bg-x1493-secondary/20 text-x1493-primary flex items-center justify-center mx-auto mb-4">{p.icon}</div>
                  <h3 className="font-bold text-lg mb-2 text-stone-900">{p.title}</h3>
                  <p className="text-sm text-stone-500 leading-relaxed">{p.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      ),
    },
    {
      id: 'join',
      render: () => (
        <section className="min-h-full bg-stone-50 flex items-center py-16">
          <div className="max-w-xl mx-auto px-6 w-full">
            <div className="text-center mb-8">
              <Sparkles size={36} className="text-x1493-primary mx-auto mb-3" />
              <h2 className="text-2xl md:text-3xl font-bold mb-2 text-stone-900">成為 YYC 推廣夥伴</h2>
              <p className="text-stone-500 text-sm">留低資料，即刻開通你嘅專屬推廣後台——零門檻、零成本。</p>
            </div>
            {done ? (
              <div className="bg-white border border-stone-200 rounded-3xl p-8 text-center">
                <CheckCircle2 size={44} className="text-green-600 mx-auto mb-4" />
                <h2 className="text-xl font-bold mb-2">歡迎加入！🎉</h2>
                <p className="text-sm text-stone-500 leading-relaxed mb-3">
                  你嘅推廣碼：<b className="text-x1493-primary">{done.referralCode}</b>
                </p>
                <p className="text-sm text-stone-500">推廣後台連結已經可以透過你嘅專屬連結使用——開始分享賺佣金啦！</p>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white border border-stone-200 rounded-3xl p-6 md:p-8 space-y-5">
                <div>
                  <label className="block text-sm font-bold text-stone-700 mb-1.5">稱呼 *</label>
                  <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} placeholder="例：阿明／Mei" required maxLength={60} />
                </div>
                <div>
                  <label className="block text-sm font-bold text-stone-700 mb-1.5">WhatsApp 號碼 *（揀你所在嘅國家）</label>
                  <CountryPhoneInput value={phone} onChange={setPhone} inputCls={inputCls} required ariaLabel="WhatsApp 號碼" />
                </div>
                <div>
                  <label className="block text-sm font-bold text-stone-700 mb-1.5">而家住喺邊個城市</label>
                  <input className={inputCls} value={city} onChange={(e) => setCity(e.target.value)} placeholder="例：曼徹斯特／溫哥華" maxLength={60} />
                </div>
                <label className="flex items-start gap-2 text-xs text-stone-500 cursor-pointer">
                  <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5" />
                  同意接收推廣同佣金相關 WhatsApp 訊息（可隨時停止）
                </label>
                {error && <p className="text-sm text-red-600">{error}</p>}
                <button type="submit" disabled={submitting} className="w-full bg-x1493-primary text-white font-bold py-3.5 rounded-2xl hover:brightness-110 transition disabled:opacity-60">
                  {submitting ? '提交中…' : '開通我嘅推廣後台'}
                </button>
              </form>
            )}
          </div>
        </section>
      ),
    },
  ];

  return (
    <SceneDeck
        scenes={scenes}
        mode="lever"
        onSceneChange={(i) => {
          trackEvent('partner_scene', { scene: i });
          if (i === scenes.length - 1) flushEvents();
        }}
      />
  );
}
